import clsx from "clsx";
import { useState } from "react";
import { useParams } from "react-router-dom";
import { useAddContactsMutation } from "../../../../../../features/contactsApi";
import { useFindAllPeopleQuery } from "../../../../../../features/peopleApi";
import { addBannerWithTimeout } from "../../../../../lib/centralizedBanner/slice";
import { Filters } from "../../../../../lib/filters";
import { PeopleHorizontalFilters } from "../../../../../lib/filters/accordion/horizontalFiltersPerson";
import { ContactType, FindAllContactsFilters, FindContactsDatabaseResponse } from "../../../../../lib/filters/dto";
import { initialStateContactsFilters } from "../../../../../lib/filters/filtersSlice";
import { useAppDispatch, useAppSelector } from "../../../../../lib/redux/hooks";
import { FiltersLinesIcon } from "../../../../../ui/icons/filtersLines";
import { PlusCircleIcon } from "../../../../../ui/icons/plusCircle";
import { UploadIcon } from "../../../../../ui/icons/upload";
import Button from "../../../../../ui/molecules/button";
import { Spinner } from "../../../../../ui/molecules/spinner";
import { NotFound } from "../../../../../ui/organisms/notFound";
import { setFiltersActive } from "../../../../database/slice";
import { AddContactsFromDatabaseModal } from "../../../contacts/addFromDatabase";
import { ContactsIdsResponseDTO } from "../../../contacts/dto";
import { TargetListPageEnum } from "../../../dto";
import { setTargetListPage } from "../../../slice";
import { ImportFromDatabaseList } from "./list";

interface Props {
  targetContactIds: ContactsIdsResponseDTO[];
}

export function ImportFromDatabase({ targetContactIds }: Props) {
  const targetId = useParams().targetId!;

  const databaseState = useAppSelector((state) => state.database);

  const [peopleFilters, setPeopleFilters] = useState<FindAllContactsFilters>(initialStateContactsFilters['add-target-db'][ContactType.PERSON]);
  const { data: peopleData, isLoading: isLoadingPeople } = useFindAllPeopleQuery(peopleFilters);

  const [addContacts, { isLoading: isAddingContacts }] = useAddContactsMutation();

  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [confirmModal, setConfirmModal] = useState<boolean>(false);

  const dispatch = useAppDispatch();

  const handleAdd = async () => {
    try {
      await addContacts({
        targetListId: targetId,
        contactType: ContactType.PERSON,
        contactIds: selectedIds
      }).unwrap();
      setSelectedIds([]);
      dispatch(setTargetListPage(TargetListPageEnum.LIST));
      dispatch(addBannerWithTimeout({
        message: "Contatti aggiunti con successo",
        variant: "success"
      }));
    }
    catch (error: any) {
      dispatch(addBannerWithTimeout({
        message: "Si è verificato un errore durante l'aggiunta dei contatti",
        variant: "error"
      }));
    }
  }

  if (isLoadingPeople) {
    return <div className="flex items-center justify-center h-screen w-full"><Spinner /></div>
  }

  return (
    <div className="flex max-h-full h-full">
      <div
        className={clsx("transition-all duration-300 w-[20%] flex-shrink-0", {
          block: databaseState.filtersActive,
          hidden: !databaseState.filtersActive,
        })}
      >
        <Filters
          section={'add-target-db'}
          contactType={ContactType.PERSON}
          isInTargetList={false}
          resetFilters={() => { setPeopleFilters(initialStateContactsFilters['add-target-db'][ContactType.PERSON]) }}
          updateFilters={(newFilters) => setPeopleFilters({ ...peopleFilters, ...newFilters, page: 0 })}
          filters={peopleFilters}
        />
      </div>
      <div
        className={clsx('h-full max-h-full', {
          "w-full": !databaseState.filtersActive,
          "w-4/5": databaseState.filtersActive,
        })}
      >
        <div className="h-full max-h-full">
          <div className="bg-white rounded-t-2xl h-16 flex items-center justify-between px-4">
            <span className="text-heading-xs font-semibold text-neutral-800">
              {"Database > Persone"}
            </span>
            <div className="flex items-center gap-2">
              {
                isAddingContacts ?
                  <Spinner /> :
                  (
                    selectedIds.length > 0 && (
                      <Button
                        size={"sm"}
                        iconPosition={"left"}
                        variant={"outline"}
                        label={"Importa " + selectedIds.length + " selezionati"}
                        color={"blue"}
                        icon={<UploadIcon color={""} size={0} />}
                        onClick={handleAdd}
                      />
                    )
                  )
              }
              <Button
                size={"sm"}
                iconPosition={"left"}
                label="Importa tutti"
                variant={"outline"}
                color={"blue"}
                icon={<PlusCircleIcon color={""} size={0} />}
                onClick={() => {
                  setConfirmModal(true)
                }}
              />
              <Button
                size={"sm"}
                iconPosition={"off"}
                label="Filtri / Espandi"
                variant={databaseState.filtersActive ? "outline" : "ghost"}
                color={databaseState.filtersActive ? "blue" : "gray"}
                icon={<FiltersLinesIcon color={""} size={0} />}
                onClick={() => {
                  dispatch(setFiltersActive(!databaseState.filtersActive));
                }}
              />
            </div>
          </div>
          <PeopleHorizontalFilters
            onChangeFilters={filters => setPeopleFilters({ ...peopleFilters, ...filters, page: 0 })}
            filters={peopleFilters}
            toActivate={peopleData?.toActivate ?? 0}
            isInTargetList={true}
            isAddingFromDatabase={true}
          />
          {peopleData?.people.total === 0 ||
            peopleData?.people.data === null ? (
            <div style={{ height: 'calc(100% - 64px - 60px)' }}>
              <NotFound />
            </div>
          ) : (
            <ImportFromDatabaseList
              targetContactIds={targetContactIds}
              filters={peopleFilters}
              selectedIds={selectedIds}
              setSelectedIds={(values) => setSelectedIds(values)}
              contacts={peopleData?.people as unknown as FindContactsDatabaseResponse<ContactType.PERSON>}
              setFilters={values => setPeopleFilters({ ...peopleFilters, ...values, page: 0 })}
              setPage={(page: number) => setPeopleFilters({ ...peopleFilters, page })}
            />
          )}
        </div>
      </div>
      <AddContactsFromDatabaseModal
        filters={peopleFilters}
        setFilters={setPeopleFilters}
        showModal={confirmModal}
        setShowModal={setConfirmModal}
        contactType={ContactType.PERSON}
        targetId={targetId}
      />
    </div>
  );
}
